import { IntercomCustomAttributeKey } from 'constants/IntercomCustomAttributeKey';
import { PackageProviderName, SettingKey } from 'data/ppoints/constants';
import { AppInstallationSubscription } from 'hooks/shopify/queries/get-all-app-subscriptions.graphql';
import { SubscriptionStatus } from 'models/SubscriptionStatus';
import { Ecom } from 'models/Ecom';
import { HomeDeliverySetting } from 'models/ppoints/settings/HomeDeliverySetting';
import { SettingsToEcom } from 'models/ppoints/settings/SettingsToEcom';
import { ShopifyShopData } from 'models/ShopifyShopData';
import { useIntercom } from 'react-use-intercom';
import { useAppSelector } from 'redux/hooks';
import { selectThirdPartyFulfillmentSettings } from 'redux/slices/third-party-fulfillment-settings/third-party-fulfillment-settings.selector';
import { PackageProvider } from '../../../pb/responses/v1/package_providers_response_pb';
import { ThirdPartyFulfillmentSettingsResponse } from '../../../pb/responses/v1/third_party_fulfillment_settings_response_pb';

export const useUpdateIntercomFields = () => {
    const { update } = useIntercom();
    const settings = useAppSelector((state) => state.settings);
    const thirdPartyFulfillmentSettings = useAppSelector(selectThirdPartyFulfillmentSettings);

    const mapProvidersToCode = (providerList: string[], packageProviders: PackageProvider[]) => {
        if (providerList.length === 0) return '';
        return providerList
            .map((provider) => {
                const foundProvider = packageProviders.find((p) => p.name === provider);
                return foundProvider?.code && foundProvider?.code != '' ? foundProvider.code : provider;
            })
            .join(', ');
    };

    const getProviders = (
        providerToCountry: SettingsToEcom[SettingKey.provider_to_country],
        packageProviders: PackageProvider[]
    ): string => {
        if (!providerToCountry) return '';
        const providers = [...new Set(Object.values(providerToCountry).flat())];
        return mapProvidersToCode(providers, packageProviders);
    };

    const getHomeDelivery = (
        homeDelivery: SettingsToEcom[SettingKey.home_delivery],
        packageProviders: PackageProvider[]
    ): string => {
        if (!homeDelivery) return '';
        const homeDeliveryProviders = Object.entries(homeDelivery)
            .filter(([provider, val]) => val.home_delivery && packageProviders.some((p) => p.name === provider))
            .map(([provider]) => provider);
        return mapProvidersToCode(homeDeliveryProviders, packageProviders);
    };

    const getPickupPoints = (
        pickupPoints: SettingsToEcom[SettingKey.shipping_methods],
        packageProviders: PackageProvider[]
    ): string => {
        if (!pickupPoints || !('providers' in pickupPoints)) return '';
        const providers = Object.entries(pickupPoints.providers ?? {})
            .filter(([, zones]) => Object.values(zones).some((rates) => rates.length > 0))
            .map(([key]) => key.replace('provider_', ''))
            .filter((provider) => packageProviders.some((p) => p.name === provider));
        return mapProvidersToCode(providers, packageProviders);
    };

    const getActiveProviders = (
        packageProviders: PackageProvider[],
        providerToCountry: SettingsToEcom[SettingKey.provider_to_country]
    ): PackageProvider[] => {
        if (!providerToCountry) return [];
        const providers = [...new Set(Object.values(providerToCountry).flat())];
        return packageProviders.filter((provider) => providers.includes(provider.name as PackageProviderName));
    };

    const getThirdPartyFulfillment = (
        thirdPartyFulfillmentSettings: ThirdPartyFulfillmentSettingsResponse,
        packageProviders: PackageProvider[]
    ): string => {
        const providers = (thirdPartyFulfillmentSettings?.settings ?? [])
            .filter((s) => s.value && packageProviders.some((p) => p.name === s.provider))
            .map((s) => s.provider);
        return mapProvidersToCode(providers, packageProviders);
    };

    const handleDataToIntercom = (
        settingsToEcom: SettingsToEcom,
        currentSubscription: AppInstallationSubscription | undefined,
        shopData: ShopifyShopData,
        thirdPartyFulfillmentSettings: ThirdPartyFulfillmentSettingsResponse,
        packageProviders: PackageProvider[],
        /** The plan's name from the backend's plans list; the subscription's own name when not given */
        planName?: string
    ) => {
        try {
            const providerToCountry = settingsToEcom[
                SettingKey.provider_to_country
            ] as SettingsToEcom[SettingKey.provider_to_country];
            const homeDelivery = (settingsToEcom?.[SettingKey.home_delivery] ?? {}) as HomeDeliverySetting;
            const pickupPoints = (settingsToEcom?.[SettingKey.shipping_methods] ??
                {}) as SettingsToEcom[SettingKey.shipping_methods];

            const activeProviders = getActiveProviders(packageProviders, providerToCountry);

            update({
                customAttributes: {
                    [IntercomCustomAttributeKey.SubscriptionPlanName]: planName || currentSubscription?.name || '',
                    ...(currentSubscription?.status && {
                        [IntercomCustomAttributeKey.Subscription]:
                            currentSubscription.status === SubscriptionStatus.CANCELLED ? 'cancelled' : 'active',
                    }),
                    [IntercomCustomAttributeKey.Providers]: getProviders(providerToCountry, activeProviders),
                    [IntercomCustomAttributeKey.HomeDelivery]: getHomeDelivery(homeDelivery, activeProviders),
                    [IntercomCustomAttributeKey.PickupPoint]: getPickupPoints(pickupPoints, activeProviders),
                    ...(shopData && {
                        [IntercomCustomAttributeKey.ShopifyPlan]: shopData.shop_plan?.displayName || '',
                    }),
                    [IntercomCustomAttributeKey.ThirdPartyFulfillment]: getThirdPartyFulfillment(
                        thirdPartyFulfillmentSettings,
                        activeProviders
                    ),
                },
            });
        } catch (error) {
            console.error('Error in handleDataToIntercom:', error);
        }
    };

    const handleProviderToCountryToIntercom = (
        providerToCountry: SettingsToEcom[SettingKey.provider_to_country],
        packageProviders: PackageProvider[]
    ) => {
        const activeProviders = getActiveProviders(packageProviders, providerToCountry);
        const homeDelivery = (settings?.settings?.[SettingKey.home_delivery] ?? {}) as HomeDeliverySetting;
        const pickupPoints = (settings?.settings?.[SettingKey.shipping_methods] ??
            {}) as SettingsToEcom[SettingKey.shipping_methods];
        update({
            customAttributes: {
                [IntercomCustomAttributeKey.Providers]: getProviders(providerToCountry, activeProviders),
                [IntercomCustomAttributeKey.HomeDelivery]: getHomeDelivery(homeDelivery, activeProviders),
                [IntercomCustomAttributeKey.PickupPoint]: getPickupPoints(pickupPoints, activeProviders),
                [IntercomCustomAttributeKey.ThirdPartyFulfillment]: getThirdPartyFulfillment(
                    thirdPartyFulfillmentSettings,
                    activeProviders
                ),
            },
        });
    };

    const handleHomeDeliveryToIntercom = (
        homeDelivery: SettingsToEcom[SettingKey.home_delivery],
        packageProviders: PackageProvider[]
    ) => {
        const activeProviders = getActiveProviders(
            packageProviders,
            settings?.settings?.[SettingKey.provider_to_country] as SettingsToEcom[SettingKey.provider_to_country]
        );
        update({
            customAttributes: {
                [IntercomCustomAttributeKey.HomeDelivery]: getHomeDelivery(homeDelivery, activeProviders),
            },
        });
    };

    const handlePickupPointsByProvidersToIntercom = (
        pickupPoints: SettingsToEcom[SettingKey.shipping_methods],
        packageProviders: PackageProvider[]
    ) => {
        const activeProviders = getActiveProviders(
            packageProviders,
            settings?.settings?.[SettingKey.provider_to_country] as SettingsToEcom[SettingKey.provider_to_country]
        );
        update({
            customAttributes: {
                [IntercomCustomAttributeKey.PickupPoint]: getPickupPoints(pickupPoints, activeProviders),
            },
        });
    };

    const handleThirdPartyFulfillmentToIntercom = (
        thirdPartyFulfillmentSettings: ThirdPartyFulfillmentSettingsResponse,
        packageProviders: PackageProvider[]
    ) => {
        const activeProviders = getActiveProviders(
            packageProviders,
            settings?.settings?.[SettingKey.provider_to_country] as SettingsToEcom[SettingKey.provider_to_country]
        );
        update({
            customAttributes: {
                [IntercomCustomAttributeKey.ThirdPartyFulfillment]: getThirdPartyFulfillment(
                    thirdPartyFulfillmentSettings,
                    activeProviders
                ),
            },
        });
    };

    return {
        handleDataToIntercom,
        handleProviderToCountryToIntercom,
        handleHomeDeliveryToIntercom,
        handlePickupPointsByProvidersToIntercom,
        handleThirdPartyFulfillmentToIntercom,
    };
};
